// @ts-nocheck
/**
 * Runtime icon resolver using webpack require.context across store, mobius, and starter directories.
 * Replaces the build-time iconHelper.js / generated iconsIndex.ts approach.
 *
 * Override priority (last spread wins): store → mobius → starter.
 * Uses "lazy-once" mode — icons are loaded on demand, one chunk per directory.
 */

type RequireContext = __WebpackModuleApi.RequireContext;

interface IconEntry {
    ctx: RequireContext;
    key: string;
}

function buildMap(ctx: RequireContext): Record<string, IconEntry> {
    const map: Record<string, IconEntry> = {};
    for (const key of ctx.keys()) {
        // Strip path to just the filename: "./AlertCircle/AlertCircleFill.tsx" → "AlertCircleFill"
        const name = key.replace(/^.*\//, "").replace(/\.tsx$/, "");
        map[name] = { ctx, key };
    }
    return map;
}

// Override priority: store (lowest) → mobius → starter (highest = last spread wins)
const storeCtx = require.context("../../../../Library/Components/Icons", true, /\.tsx$/, "lazy-once");
const mobiusCtx = require.context(
    "../../../../../../../mobius/src/Icons",
    true,
    /\/(?!commonIcons)\w+\.tsx$/,
    "lazy-once",
);
const starterCtx = require.context(
    "../../../../../../starter/src/Library/Components/Toolkit/Icons",
    true,
    /\.tsx$/,
    "lazy-once",
);

const iconMap: Record<string, IconEntry> = {
    ...buildMap(storeCtx),
    ...buildMap(mobiusCtx),
    ...buildMap(starterCtx),
};

/** All resolved icon names, sorted alphabetically. */
export const iconNames: string[] = Object.keys(iconMap).sort();

/** Check if a given name is a valid icon key. */
export function hasIcon(name: string): boolean {
    return name in iconMap;
}

/** Lazily resolve an icon component by name. Returns undefined for unknown names. */
export async function resolveIcon(name: string): Promise<React.ComponentType | undefined> {
    const entry = iconMap[name];
    if (!entry) {
        return undefined;
    }
    const mod = await entry.ctx(entry.key);
    return (mod as { default: React.ComponentType }).default;
}

/**
 * Get a React.lazy-compatible loader for the given icon name.
 * Returns undefined for unknown names.
 */
export function getIconLoader(name: string): (() => Promise<{ default: React.ComponentType }>) | undefined {
    const entry = iconMap[name];
    if (!entry) {
        return undefined;
    }
    return () => entry.ctx(entry.key) as Promise<{ default: React.ComponentType }>;
}
